// @flow
import * as React from 'react'
import moment from 'moment-timezone'
import isEmpty from 'lodash/isEmpty'
import type { Coordinates, Gathering, School } from 'types'
import { AppContext, type AppContextValue, type AsyncResource, FailedToLoad } from 'framework'
import { formatAddress } from 'util/address'
import ImageHero, { ImageHeroContent } from 'ui/Marcom/ImageHero'
import { DesktopMediaQuery, MobileMediaQuery } from 'ui/MediaQuery'
import { PageShell } from 'ui/Page'
import Loader from 'ui/Loader'
import NoMatch from 'ui/NoMatch'
import RegisterButton from 'pages/home/shared/RegisterButton'
import GatheringPageDesktopContent from './DesktopContent'
import GatheringPageMobileContent from './MobileContent'
import { RegistrationQuery } from '../../../gql/school'

type Props = {
  gathering: AsyncResource<Gathering>,
  coordinates: AsyncResource<Coordinates>,
  school: AsyncResource<School>,
}

const lego = window.lego

export default class GatheringPagePresentation extends React.Component<Props> {
  static contextType = AppContext
  context: AppContextValue

  render = () => {
    return (
      <PageShell
        title={this.pageTitle}
        description={this.pageDescription}
        metatags={this.pageMetatags}
        contentWidthConstraint={false}
      >
        <div className="GatheringPage-container">{this.renderContent()}</div>
      </PageShell>
    )
  }

  renderContent = () => {
    const { coordinates, gathering, school } = this.props
    if (gathering === FailedToLoad) return <NoMatch />
    if (school === FailedToLoad) return <NoMatch />
    if (!gathering || !school) return <Loader />

    return (
      <div>
        {this.renderHero(gathering)}
        <MobileMediaQuery>
          <GatheringPageMobileContent
            coordinates={coordinates}
            gathering={gathering}
            school={school}
          />
        </MobileMediaQuery>
        <DesktopMediaQuery>
          <GatheringPageDesktopContent
            coordinates={coordinates}
            gathering={gathering}
            school={school}
          />
        </DesktopMediaQuery>
      </div>
    )
  }

  get pageTitle(): string | void {
    const { gathering, school } = this.props
    if (!gathering || !school) return undefined

    const when = moment(gathering.beginsAt).tz(school.timezone).format('ddd, MMM DD [at] h:mma')

    return `${gathering.name}, ${when} | Wonderschool`
  }

  get pageDescription(): string {
    return (this.props.gathering && this.props.gathering.description) || ''
  }

  get pageMetatags(): {}[] {
    const fallbackImgUrl =
      'https://ucarecdn.com/2ebf7e06-dd17-444b-bb18-09adf1ed3870/schoolprofiletop.png'
    const imageUrl =
      this.props.gathering === FailedToLoad || !this.props.gathering
        ? fallbackImgUrl
        : this.gatheringPhoto(this.props.gathering)

    return [
      { property: 'og:title', content: this.pageTitle },
      { property: 'og:description', content: this.pageDescription },
      { property: 'og:image', content: imageUrl },
    ]
  }

  renderHero = (gathering: Gathering) => {
    return (
      <ImageHero imageUrl={this.gatheringPhoto(gathering)}>
        <ImageHeroContent
          title={gathering.name}
          body={formatAddress(gathering.address)}
          actions={this.renderInterestedButton(gathering)}
        />
      </ImageHero>
    )
  }

  renderInterestedButton = (gathering: Gathering) => {
    return (
      <RegistrationQuery variables={{ gatheringId: gathering.id }}>
        {({ data, error, loading }) => {
          const registrations = error || isEmpty(data) || loading ? [] : data.gatheringRegistrations
          return <RegisterButton gathering={gathering} registration={registrations} />
        }}
      </RegistrationQuery>
    )
  }

  gatheringPhoto(gathering: Gathering): string {
    if (!gathering.photos || !gathering.photos.length) {
      return `${lego.config.ASSET_DOMAIN}/images/school/school-profile-top.jpg`
    }

    return gathering.photos[0]
  }
}
